import * as React from "react";
import {
    DateFormats,
    IClientBooking,
    IIntakeSessionHistory,
} from "intake_shared/src/types";
import moment from "../../../../../../utils/app_moment";
import { Button } from "../../../../bits/Button/Button";
import { findLabelByPracticeId, toTitleCase } from "../../../../../../utils/utils";
import styles from "../AppointmentHistory.module.scss";
import { Badge } from "../../../../bits/Badge/Badge";
import { getV2BadgePropsByPractice } from "../../../../../../constants";
import ChevronUpIcon from "../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import { cn } from "../../../../../helpers";
import ServiceIcon from "../../../../icons/Service";
import ClockIcon from "../../../../icons/Clock";
import LocationIcon from "../../../../icons/Location";
import { InfoIcon } from "../../../../icons/Info";
import { safeToDate } from "../../../../../v2utils";

// DetailItem component
interface DetailItemProps {
    label: string;
    value: string | React.ReactNode;
    className?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value, className }) => {
    return (
        <div className={cn(styles.detailGroup)}>
            <span className={styles.label}>{label}</span>
            <span className={styles.value}>{value}</span>
        </div>
    );
};

interface IntakeSessionHistoryCardProps {
    appointment: IIntakeSessionHistory;
    viewFrom: "side-bar" | "full-page";
    isExpanded: boolean;
    onToggleExpand: () => void;
    allDepartmentsAndServices: any;
    attachedAppointment?: IClientBooking;
}

const IntakeSessionHistoryCard: React.FC<IntakeSessionHistoryCardProps> = ({
    appointment,
    viewFrom = "side-bar",
    isExpanded,
    onToggleExpand,
    allDepartmentsAndServices,
    attachedAppointment,
}) => {
    const formatDate = (date: string) =>
        moment(date).format(DateFormats.DAY_MONTH_FORMAT);
    const formatTime = (date: string) =>
        moment(date).format(DateFormats.HOUR_MIN_FORMAT);

    const appointmentPractice = findLabelByPracticeId(
        appointment?.practice_id ||
            (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
        allDepartmentsAndServices
    );

    const actionedOnDate = safeToDate(appointment.actioned_on);
    const attachedAppointmentPracticeId =
        appointment.booking_practice_id || attachedAppointment?.practice_id || "";
    const attachedAppointmentPractice = attachedAppointmentPracticeId
        ? findLabelByPracticeId(
              attachedAppointmentPracticeId,
              allDepartmentsAndServices
          )
        : "";
    const attachedAppointmentStart =
        appointment.booking_start || attachedAppointment?.start;
    const attachedAppointmentStartingDate =
        appointment.booking_starting_date || attachedAppointment?.starting_date;
    const attachedAppointmentDateTime = attachedAppointmentStart
        ? moment(attachedAppointmentStart).format("ddd MMM Do, h:mma")
        : attachedAppointmentStartingDate
        ? moment(attachedAppointmentStartingDate).format("ddd MMM Do")
        : "";
    const attachedAppointmentService =
        appointment.booking_requested_service ||
        attachedAppointment?.requested_service ||
        "";
    const attachedAppointmentClinician =
        appointment.booking_clinician_display_name ||
        attachedAppointment?.clinician_display_name ||
        "";
    const attachedAppointmentClinicianText =
        attachedAppointmentClinician && attachedAppointmentPractice
            ? `${attachedAppointmentClinician} (${attachedAppointmentPractice})`
            : attachedAppointmentClinician;
    const attachedAppointmentServiceText =
        attachedAppointmentService || "Appointment";
    const hasAttachedAppointment = Boolean(
        attachedAppointmentService ||
            attachedAppointmentClinician ||
            attachedAppointmentDateTime
    );

    const serviceDeliveryOptionLabels: { [key: string]: string } = {
        in_person: "In Person",
        telephone: "Telephone",
        video_call: "Video Call",
        fax: "Fax",
        mail: "Mail",
        other: "Other",
    };
    const clinicianCredential = (
        appointment as IIntakeSessionHistory & {
            clinician_credential?: string;
        }
    ).clinician_credential;

    return viewFrom === "side-bar" ? (
        <div className={styles.appointmentCard}>
            <div className={styles.appointmentHeader}>
                <div className={styles.appointmentInfo}>
                    <div className={styles.appointmentBadges}>
                        <p className={styles.appointmentClinician}>
                            {appointment.clinician_display_name}
                        </p>
                        {clinicianCredential && (
                            <Badge tone="info" content={clinicianCredential} />
                        )}
                    </div>
                    <div className={styles.appointmentBadges}>
                        <Badge
                            content={appointmentPractice}
                            textColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .textColor
                            }
                            bgColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .bgColor
                            }
                        />

                        {appointment.action && (
                            <Badge tone="danger" content={appointment.action} />
                        )}
                    </div>
                    {appointment.actioned_on && (
                        <DetailItem
                            label="Actioned on:"
                            value={moment(actionedOnDate).format(
                                DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                            )}
                        />
                    )}
                </div>

                {!isExpanded && (
                    <Button
                        variant="normal"
                        onClick={onToggleExpand}
                        className={styles.toggleButton}
                    >
                        View more <ChevronDownIcon stroke="#1A738D" />
                    </Button>
                )}
            </div>

            {isExpanded && (
                <div className={styles.hiddenContent}>
                    <div>
                        <DetailItem
                            label="Actioned by:"
                            value={appointment.actioned_by}
                        />

                        <DetailItem
                            label="Service:"
                            value="Supervisor Intake Session"
                        />

                        {hasAttachedAppointment && (
                            <DetailItem
                                label="Attached appointment:"
                                value={
                                    <>
                                        <span>{attachedAppointmentServiceText}</span>
                                        {attachedAppointmentClinicianText && (
                                            <>
                                                <br />
                                                <span>
                                                    Clinician:{" "}
                                                    {
                                                        attachedAppointmentClinicianText
                                                    }
                                                </span>
                                            </>
                                        )}
                                        {attachedAppointmentDateTime && (
                                            <>
                                                <br />
                                                <span>
                                                    {attachedAppointmentDateTime}
                                                </span>
                                            </>
                                        )}
                                    </>
                                }
                            />
                        )}

                        <DetailItem
                            label="Location:"
                            value={appointment.location || "Remote"}
                        />

                        {appointment.service_delivery_option && (
                            <DetailItem
                                label="Delivery Option:"
                                value={toTitleCase(
                                    appointment.service_delivery_option?.replace(
                                        "_",
                                        " "
                                    ) || "N/A"
                                )}
                            />
                        )}

                        <DetailItem
                            label="Starts:"
                            value={`${formatDate(
                                appointment.start_time
                            )} at ${formatTime(appointment.start_time)}`}
                        />

                        {appointment.recurrence && (
                            <DetailItem
                                label="Recurrence:"
                                value={appointment.recurrence}
                            />
                        )}
                    </div>
                    <div className={styles.notesSection}>
                        <div className={styles.notesContainer}>
                            <span className={styles.notesLabel}>Action notes:</span>
                            <span className={styles.notesValue}>
                                {appointment.reason}
                            </span>
                        </div>
                    </div>

                    {isExpanded && (
                        <Button
                            variant="normal"
                            onClick={onToggleExpand}
                            className={styles.toggleButton}
                        >
                            View less <ChevronUpIcon stroke="#1A738D" />
                        </Button>
                    )}
                </div>
            )}
        </div>
    ) : (
        <div className={styles.apptHistoryPageContainer}>
            <div className={styles.appointmentHeader}>
                <div className={styles.clinicianInfo}>
                    <div className={styles.appointmentBadges}>
                        <p className={styles.clinicianName}>
                            {appointment.clinician_display_name}
                        </p>
                        {clinicianCredential && (
                            <Badge tone="info" content={clinicianCredential} />
                        )}
                    </div>
                    <div className={styles.clinicianBadges}>
                        <Badge
                            content={appointmentPractice}
                            textColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .textColor
                            }
                            bgColor={
                                getV2BadgePropsByPractice(appointmentPractice)
                                    .bgColor
                            }
                        />
                        {appointment.action && (
                            <Badge tone="danger" content={appointment.action} />
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.detailsGrid}>
                <div className={styles.detailCard}>
                    <div className={styles.detailIcon}>
                        <ServiceIcon />
                    </div>
                    <div className={styles.detailContent}>
                        <div className={styles.detailLabel}>Service</div>
                        <div className={styles.detailValue}>
                            Supervisor Intake Session
                        </div>
                        {appointment.service_delivery_option && (
                            <div className={styles.detailSubtext}>
                                {serviceDeliveryOptionLabels[
                                    appointment.service_delivery_option || ""
                                ] ||
                                    toTitleCase(
                                        appointment.service_delivery_option?.replace(
                                            "_",
                                            " "
                                        ) || "N/A"
                                    )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.detailCard}>
                    <div className={styles.detailIcon}>
                        <ClockIcon />
                    </div>
                    <div className={styles.detailContent}>
                        <div className={styles.detailLabel}>Time</div>
                        <div className={styles.detailValue}>
                            {formatDate(appointment.start_time)} at{" "}
                            {formatTime(appointment.start_time)}
                        </div>
                        {appointment.recurrence && (
                            <div className={styles.detailSubtext}>
                                {appointment.recurrence}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.detailCard}>
                    <div className={styles.detailIcon}>
                        <LocationIcon />
                    </div>
                    <div className={styles.detailContent}>
                        <div className={styles.detailLabel}>Location</div>
                        <div className={styles.detailValue}>
                            {appointment.location || "Remote"}
                        </div>
                    </div>
                </div>

                {appointment.actioned_on && (
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <InfoIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>Action</div>
                            <div className={styles.detailSubtext}>
                                {toTitleCase(appointment.action)}
                            </div>
                            <div className={styles.detailValue}>
                                {moment(actionedOnDate).format(
                                    DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                                )}
                            </div>
                            <div className={styles.detailSubtext}>
                                By: {appointment.actioned_by}
                            </div>
                        </div>
                    </div>
                )}

                {hasAttachedAppointment && (
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <InfoIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>
                                Attached Appointment
                            </div>
                            <div className={styles.detailValue}>
                                {attachedAppointmentServiceText}
                            </div>
                            {attachedAppointmentClinicianText && (
                                <div className={styles.detailSubtext}>
                                    Clinician: {attachedAppointmentClinicianText}
                                </div>
                            )}
                            {attachedAppointmentDateTime && (
                                <div className={styles.detailSubtext}>
                                    {attachedAppointmentDateTime}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className={cn(styles.notesSection, styles.notesSectionPage)}>
                <div
                    className={cn(styles.notesContainer, styles.notesContainerPage)}
                >
                    <span className={styles.notesLabel}>Action notes:</span>
                    <span className={styles.notesValue}>{appointment.reason}</span>
                </div>
            </div>
        </div>
    );
};

export default IntakeSessionHistoryCard;
